import {
  createAsyncThunk,
  createSelector,
  createSlice
} from '@reduxjs/toolkit';
import memoize from 'lodash/memoize';
import { retrieveMetadata } from '../api/metadata.api';
import { transformObjectArrayToObject } from '../helpers/array.helpers';
import {
  CoreState,
  MediaMetadata,
  MetadataName,
  MetadataState,
  SubtenantsMetadata
} from '../interfaces';
import { DiscountsMetadata } from '../interfaces/metadata';
import { ActionCache } from '../utility/actionCache';

const cache = new ActionCache<string, any>();
const offeringCache = new ActionCache<string, any>();
const productCache = new ActionCache<string, any>();
const usageRulesCache = new ActionCache<string, any>();
const caseActivityCache = new ActionCache<string, any>();
const discountsCache = new ActionCache<
  string,
  { Discounts: DiscountsMetadata[] }
>();
const mediaCache = new ActionCache<string, { Media: MediaMetadata }>();

const fetchMetadataThunk = createAsyncThunk<any, MetadataName, any>(
  'metadata',
  async (metaDataName) => {
    return retrieveMetadata<any>({ path: `${metaDataName}` });
  }
);

const fetchOfferingMetadataThunk = createAsyncThunk(
  'metadata/offering',
  async (id: string) => {
    return retrieveMetadata<any>({
      path: `Offering/id/${id}/JsonLongAsString/true`
    });
  }
);

export const fetchProductMetadataThunk = createAsyncThunk(
  'metadata/product',
  async (id: string) => {
    return retrieveMetadata<any>({
      path: `Product/id/${id}/JsonLongAsString/true`
    });
  }
);

export const fetchUsageRulesForPricingPlanMetadataThunk = createAsyncThunk(
  'metadata/usageRules',
  async (id: string) => {
    return retrieveMetadata<any>({
      path: `UsageRulesForPricingPlan/PricingPlanId/${id}/JsonLongAsString/true`
    });
  }
);

export const fetchCaseTypeActivitiesMetadataThunk = createAsyncThunk(
  'metadata/caseActivity',
  async (id: string) => {
    return retrieveMetadata<any>({
      path: `CaseActivities/CaseCategoryCode/${id}/JsonLongAsString/true`
    });
  }
);

export const fetchDiscountsMetadataThunk = createAsyncThunk(
  'metadata/discounts',
  async () => {
    return retrieveMetadata<{ Discounts: DiscountsMetadata[] }>({
      path: 'Discounts/JsonLongAsString/true'
    });
  }
);

export const fetchMediaMetadataThunk = createAsyncThunk(
  'metadata/media',
  async (id: string) => {
    return retrieveMetadata<{ Media: MediaMetadata }>({
      path: `Media/Id/${id}`
    });
  }
);

/**
 * Load a metadata if it is not already loaded, return cached promise
 */
export function loadMetadata(metadataName: MetadataName) {
  return async (dispatch: Function) => {
    return cache.Load(metadataName, dispatch, fetchMetadataThunk);
  };
}

export function loadOfferingMetadata(id: string) {
  return async (dispatch: Function) => {
    return offeringCache.Load(id, dispatch, fetchOfferingMetadataThunk);
  };
}

export function loadProductMetadata(id: string) {
  return async (dispatch: Function) => {
    return productCache.Load(id, dispatch, fetchProductMetadataThunk);
  };
}

export function loadUsageRulesForPricingPlanMetadata(id: string) {
  return async (dispatch: Function) => {
    return usageRulesCache.Load(
      id,
      dispatch,
      fetchUsageRulesForPricingPlanMetadataThunk
    );
  };
}

export function loadCaseTypeActivitiesMetadata(id: string) {
  return async (dispatch: Function) => {
    return caseActivityCache.Load(
      id,
      dispatch,
      fetchCaseTypeActivitiesMetadataThunk
    );
  };
}

export function loadDiscountsMetadata() {
  return async (dispatch: Function) => {
    return discountsCache.Load(
      'discounts',
      dispatch,
      fetchDiscountsMetadataThunk
    );
  };
}

export function loadMediaMetadata(id: string) {
  return async (dispatch: Function) => {
    return mediaCache.Load(id, dispatch, fetchMediaMetadataThunk);
  };
}

const slice = createSlice({
  name: 'metadata',
  initialState: {
    CaseActivitiesByCaseType: {},
    Discounts: {},
    Offering: {},
    Media: {},
    Product: {},
    UsageRules: {},
    Subtenants: []
  } as MetadataState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMetadataThunk.fulfilled, (state, action) => {
        state[action.meta.arg] = action.payload[action.meta.arg];
      })
      .addCase(fetchOfferingMetadataThunk.fulfilled, (state, action) => {
        state.Offering[action.meta.arg] = action.payload.Offering;
      })
      .addCase(fetchProductMetadataThunk.fulfilled, (state, action) => {
        state.Product[action.meta.arg] = action.payload.Product;
      })
      .addCase(
        fetchUsageRulesForPricingPlanMetadataThunk.fulfilled,
        (state, action) => {
          state.UsageRules[action.meta.arg] = action.payload;
        }
      )
      .addCase(
        fetchCaseTypeActivitiesMetadataThunk.fulfilled,
        (state, action) => {
          state.CaseActivitiesByCaseType[action.meta.arg] =
            transformObjectArrayToObject(action.payload.CaseActivities, 'Id');
        }
      )
      .addCase(fetchMediaMetadataThunk.fulfilled, (state, action) => {
        state.Media[action.meta.arg] = action.payload.Media;
      })
      .addCase(fetchDiscountsMetadataThunk.fulfilled, (state, action) => {
        state.Discounts = transformObjectArrayToObject(
          action.payload.Discounts,
          'Id'
        );
      });
  }
});

export default slice.reducer;

//Selectors
const getAllMetadata = (state: CoreState) => state.core.metadata;

export const getSubtenantsMetadata = createSelector(
  [getAllMetadata],
  (metadata: MetadataState) => metadata.Subtenants
);

export const getSubtenantsMetadataDictionary = createSelector(
  [getSubtenantsMetadata],
  (subtenants: SubtenantsMetadata[]) =>
    subtenants.reduce(
      (subtenantDictionary, subtenant) => {
        subtenantDictionary[subtenant.Id.Value] = subtenant;
        return subtenantDictionary;
      },
      {} as { [key: string]: SubtenantsMetadata }
    )
);

const getAllOfferingData = createSelector(
  [getAllMetadata],
  (metadata) => metadata.Offering
);

export const getAllProductMetadata = createSelector(
  [getAllMetadata],
  (metadata) => metadata.Product
);

export const getProductMetadata = memoize((id: string) => {
  return createSelector(
    [getAllProductMetadata],
    (productMetadata) => productMetadata[id]
  );
});

export const getProductPricingPlanMapMetadata = memoize((id: string) => {
  return createSelector([getAllProductMetadata], (productMetadata) =>
    transformObjectArrayToObject(productMetadata[id]?.PricingPlans || [], 'Id')
  );
});

export const getUsageRulesMetadata = createSelector(
  [getAllMetadata],
  (metadata) => metadata.UsageRules
);

export const getPricingPlanUsageRules = memoize((id: string) => {
  return createSelector([getUsageRulesMetadata], (usageRulesMetadata) => {
    return usageRulesMetadata[id]?.UsageRules;
  });
});

export const getPricingPlanUsageRuleIds = memoize((id: string) => {
  return createSelector([getUsageRulesMetadata], (usageRulesMetadata) => {
    return usageRulesMetadata[id]?.UsageRuleIdsForBillerRuleInstanceIds[0]
      ?.UsageRuleIds;
  });
});

/**
 * @deprecated: use useOfferingMetadata
 */
export const getOfferingMetadata = memoize((id: string) => {
  return createSelector(
    [getAllOfferingData],
    (offeringsMetadata) => offeringsMetadata[id]
  );
});

/**
 * @deprecated: use useOfferingProductMetadataDictionary
 */
export const getOfferingProductsMetadata = memoize((offerId: string) => {
  return createSelector(
    [getOfferingMetadata(offerId)],
    (offeringMetadata) => offeringMetadata?.ProductThumbnails || {}
  );
});

/**
 * @deprecated: use useOfferingPricingPlanMetadataDictionary
 */
export const getOfferingPricingPlansMetadata = memoize((offerId: string) => {
  return createSelector(
    [getOfferingMetadata(offerId)],
    (offeringMetadata) => offeringMetadata?.PricingPlanThumbnails || {}
  );
});

const getAllCaseActivitiesData = createSelector(
  [getAllMetadata],
  (metadata) => metadata.CaseActivitiesByCaseType
);

export const getCaseActivityMapByCaseTypeMetadata = memoize(
  (caseCategoryId: string) => {
    return createSelector(
      [getAllCaseActivitiesData],
      (caseActivitiesMetadata) => caseActivitiesMetadata[caseCategoryId]
    );
  }
);

export const getDiscountsMetadata = createSelector(
  [getAllMetadata],
  (metadata) => metadata.Discounts
);

const getAllMediaData = createSelector(
  [getAllMetadata],
  (metadata) => metadata.Media
);

export const getMediaMetadata = memoize((mediaId: string) => {
  return createSelector(
    [getAllMediaData],
    (mediaMetadata) => mediaMetadata[mediaId]
  );
});
